/**
 * The quote builder (docs/market-making-ux.md §1 + Revisions 1 and 2). One tight column, top to
 * bottom: margin pills + contracts → the two computed bids side by side → the big amber Draft
 * number → [Place 2 bids] → Options (collapsed).
 *
 *   inputs      the margin you want after fees (1 / 2 / 3 / 5 % or custom; 2% default) and the
 *               contracts per side (10). greenbook computes both bids (lib/marketMaking.ts
 *               `planBids`): centred on the normalised consensus, each held 1¢ under its venue's
 *               ask so it rests, 2–98¢, maker fees in. The bids stay editable; the margin follows.
 *   side cards  per side: market now · your bid ‹ › · "on <venue> ▾" (auto: the venue with the
 *               highest price, so the bid sits closest to the trading). One odds format throughout.
 *   Draft KPI   the shared MmKpi in its amber, dashed Draft state: what you make if both sides fill,
 *               then (small) the one-side-only risk and the total cost. A re-quote counts the
 *               opportunity's existing fills.
 *   Options     (collapsed) more venues per side (one compact row each; the side's contracts split
 *               across them) and different contracts per side.
 *
 *   steps       before the quote: Game (search + league pills, sectioned by start time) → Market →
 *               Participant (props / per-participant 3-way) → Line, one at a time under a tappable
 *               breadcrumb. A step with exactly one choice is skipped (and gets no crumb); the logic
 *               is lib/marketMaking.ts `resolveQuoteFlow`; the steps are the shared
 *               components/MarketPicker.tsx. A re-quote opens on the quote (or on the
 *               line step with a note when its line is gone); a given game opens on Market.
 *
 *   new quote   no opportunity id: the buy creates the opportunity (the server issues every id),
 *               filed under the Market making model (`source.ensureModel()`, which creates the
 *               model on the account's first quote; refused → placed without one, local-only)
 *   re-quote    prefilled with the opportunity's market, resting bids and fills; places with its
 *               id (and its model, or none when it has none) so the legs join it, optionally
 *               cancelling its resting legs first
 *
 * Every leg carries an explicit mode: the global Paper/Real switch, or Paper when the walkthrough's
 * "Try it with paper" opened the builder (a local override; the global mode isn't touched). Real
 * follows the ticket's arming (global Real + trade:execute) and adds a confirm listing every
 * order; if the market moved the bids between opening it and pressing Confirm, nothing is sent
 * and the confirm shows the new bids first. Nothing is sent except from the Place / Confirm buttons.
 */
import { useMemo, useState, type ReactNode } from "react";
import type { MoneyMode } from "@openmarketsai/order-sheet";
import {
  buildLadder,
  buildQuoteLegs,
  clampCents,
  DEFAULT_CONTRACTS,
  DEFAULT_MARGIN_PCT,
  fairFor,
  legRefsFrom,
  MARGIN_PRESETS,
  marketTitle,
  MAX_MARGIN_PCT,
  pairMargin,
  placedLegs,
  planBids,
  bookParticipantId,
  quoteResult,
  sideVenues,
  splitContracts,
  type BidPlan,
  type Existing,
  type Ladder,
  type MmEntry,
  type MmBook,
  type MmMarket,
  type PlacedLeg,
  type QuotePick,
  type QuoteLeg,
  type QuoteResult,
  type RequoteBid,
  draftKpi,
  type SideVenue,
} from "../../lib/marketMaking.js";
import { fmtPrice } from "../../lib/format.js";
import { useOddsFormat } from "../../lib/odds.js";
import type { Ccy } from "../../lib/portfolio.js";
import { SoftCard, Skeleton } from "../kit.js";
import { PickerCrumbs, PickerSteps, pickCrumbs, useMarketPick } from "../MarketPicker.js";
import { VenueLogo } from "../VenueLogo.js";
import { CoachTip, coachRing, type Coach } from "./Walkthrough.js";
import { MmKpi } from "./MmKpi.js";
import {
  btnBase,
  btnIdle,
  btnPrimary,
  ccyOf,
  CentsStepper,
  ConfirmOrders,
  ContractsInput,
  errorText,
  fmtBid,
  fmtContracts,
  gameLabel,
  money,
  PlacedResult,
  signedMoney,
  toneOf,
  type MarketMakingSource,
} from "./shared.js";

/** What a re-quote opens with. */
export interface QuotePrefill {
  entry: MmEntry;
  /** The opportunity's resting bids: side, venue, price, contracts left. */
  bids: RequoteBid[];
  /** Its resting router_order_ids (cancelled first when the box is ticked). */
  restingIds: string[];
  /** What it already holds, counted in the result line. */
  existing: Existing;
}

/** Open a new quote further along the steps (with `contestId`): a market, participant, line. */
export type QuoteStart = Partial<Pick<QuotePick, "marketKey" | "participantId" | "bookKey">>;

export function QuoteBuilder({
  source,
  prefill,
  contestId: initialContest,
  paper = false,
  initialMargin,
  start,
  coach,
  onPlaced,
  onClose,
}: {
  source: MarketMakingSource;
  prefill?: QuotePrefill;
  /** Open on this game's Market step (the walkthrough's example). */
  contestId?: string | null;
  /** Paper for this quote only (the walkthrough's "Try it with paper"); the global mode is untouched. */
  paper?: boolean;
  /** Start on this margin (%) instead of 2%. */
  initialMargin?: number;
  /** With `contestId`: open on this market / participant / line. */
  start?: QuoteStart;
  /** Coach marks; they only show on the quote step. */
  coach?: Coach;
  /** Placed: the opportunity id, each leg's result, and the money it was in. */
  onPlaced: (opportunityId: string, legs: PlacedLeg[], ccy: Ccy) => void;
  onClose: () => void;
}) {
  const global = source.useMode();
  const mode: MoneyMode = paper ? "paper" : global.mode;
  const wantsReal = paper ? false : global.wantsReal;
  const orders = source.useOrders();
  const ccy = ccyOf(mode);
  const requote = prefill?.entry ?? null;
  const mismatch = requote != null && requote.ccy !== ccy;

  // ── the pick: game → market → participant → line (a re-quote opens on its own book) ──
  const picker = useMarketPick(source, () =>
    requote
      ? { contestId: requote.contestId, marketKey: requote.marketKey, participantId: bookParticipantId(requote.bookKey), bookKey: requote.bookKey }
      : { marketKey: null, participantId: null, bookKey: null, ...(initialContest ? start : null), contestId: initialContest ?? null },
  );
  const { pick, contest, flow } = picker;
  const game = contest ? gameLabel(contest) : (requote?.game ?? "");
  const crumbs = pickCrumbs(picker, game, { fixedGame: !!requote });
  const gone =
    requote && flow.missing
      ? flow.missing === "line"
        ? "Its line isn't quoted any more"
        : flow.missing === "participant"
          ? "Its player isn't quoted any more"
          : "Its market isn't quoted any more"
      : null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-[14px] font-semibold text-ink">{requote ? "Re-quote" : "New quote"}</h2>
        <span
          className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${mode === "real" ? "bg-down/15 text-down" : "bg-amber/15 text-amber"}`}
          title={paper ? "Paper for this quote only" : "Follows the Paper / Real switch in the top bar"}
        >
          {mode === "real" ? "Real · USD" : "Paper · ATLAS"}
        </span>
        {paper && global.mode === "real" && <span className="text-[11px] text-faint">Just this quote. Your trading mode stays Real.</span>}
        <button type="button" onClick={onClose} aria-label="Close" title="Close" className={`${btnBase} ${btnIdle} ml-auto w-11 !px-0 lg:w-8`}>
          ✕
        </button>
      </div>

      <PickerCrumbs crumbs={crumbs} kickoff={contest?.starts_at ?? null} />

      {mismatch && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber/40 bg-amber/5 p-3 text-[12px] text-amber">
          <span className="min-w-0 flex-1">
            This opportunity is {requote!.ccy === "ATLAS" ? "Paper" : "Real"}, but your trading mode is {mode === "real" ? "Real" : "Paper"}. New legs have to be in the same money.
          </span>
          <button type="button" onClick={() => global.setMode(requote!.ccy === "ATLAS" ? "paper" : "real")} className={`${btnBase} ${btnPrimary}`}>
            Switch to {requote!.ccy === "ATLAS" ? "Paper" : "Real"}
          </button>
        </div>
      )}

      {flow.step !== "quote" || picker.gb.isError ? (
        <PickerSteps source={source} state={picker} note={gone} />
      ) : flow.market && flow.book ? (
        <QuoteDesk
          key={`${pick.contestId}|${mode}`}
          source={source}
          contestId={pick.contestId!}
          game={game}
          market={flow.market}
          book={flow.book}
          prefill={prefill}
          mode={mode}
          wantsReal={wantsReal}
          orders={orders}
          blocked={mismatch}
          initialMargin={initialMargin}
          coach={coach}
          onPlaced={(id, legs) => onPlaced(id, legs, ccy)}
        />
      ) : null}
    </div>
  );
}

/* ====================================================================== desk */

/** Per-side choices the user made; null = greenbook's pick. */
interface DeskChoice {
  /** Book this applies to (`marketKey|bookKey`); reset when the book changes. */
  id: string;
  venue: [string | null, string | null];
  bid: [number | null, number | null];
  /** Extra venues switched on per side: partnerId → bid (null = follow the side's bid). */
  extras: [Record<string, number | null>, Record<string, number | null>];
  contracts: [number, number];
  perSide: boolean;
}

const pctText = (m: number): string => {
  const p = Math.round(m * 1000) / 10;
  return `${p > 0 ? "" : p < 0 ? "−" : ""}${Math.abs(p)}%`;
};

function QuoteDesk({
  source,
  contestId,
  game,
  market,
  book,
  prefill,
  mode,
  wantsReal,
  orders,
  blocked,
  initialMargin,
  coach,
  onPlaced,
}: {
  source: MarketMakingSource;
  contestId: string;
  game: string;
  /** The market + book the steps above picked (controlled). */
  market: MmMarket;
  book: MmBook;
  prefill?: QuotePrefill;
  mode: MoneyMode;
  wantsReal: boolean;
  orders: ReturnType<MarketMakingSource["useOrders"]>;
  blocked: boolean;
  initialMargin?: number;
  coach?: Coach;
  onPlaced: (opportunityId: string, legs: PlacedLeg[]) => void;
}) {
  const ccy = ccyOf(mode);
  const { format } = useOddsFormat();
  const scope = source.useScope(mode);
  const requote = prefill?.entry ?? null;
  const ladder = useMemo(
    () => (book && market ? buildLadder(book, market.shape, { mode, allowed: scope.allowed, limitVenues: scope.limitVenues }) : null),
    [book, market, mode, scope.allowed, scope.limitVenues],
  );
  const ladderId = market && book ? `${market.marketKey}|${book.key}` : null;
  const ownBook = requote != null && ladderId === `${requote.marketKey}|${requote.bookKey}`;
  const venues = useMemo<[SideVenue[], SideVenue[]]>(() => (ladder ? [sideVenues(ladder, 0), sideVenues(ladder, 1)] : [[], []]), [ladder]);

  // ── inputs ──
  const [marginPct, setMarginPct] = useState<number>(initialMargin ?? DEFAULT_MARGIN_PCT);
  const [customText, setCustomText] = useState<string>(initialMargin != null && !(MARGIN_PRESETS as readonly number[]).includes(initialMargin) ? String(initialMargin) : "");
  const [choice, setChoice] = useState<DeskChoice | null>(null);
  if (ladderId && ladder && choice?.id !== ladderId) {
    setChoice(initialChoice(ladderId, venues, ownBook ? prefill : undefined));
  }
  const ch = choice?.id === ladderId ? choice : null;
  const patch = (fn: (c: DeskChoice) => DeskChoice) => setChoice((c) => (c ? fn(c) : c));
  const setMargin = (pct: number) => {
    setMarginPct(pct);
    // A new target recomputes both bids (and the extra venues follow them).
    patch((c) => ({ ...c, bid: [null, null], extras: [nullBids(c.extras[0]), nullBids(c.extras[1])] }));
  };

  // ── the plan ──
  const picked: [SideVenue | undefined, SideVenue | undefined] = [
    venues[0].find((v) => v.partnerId === ch?.venue[0]) ?? venues[0][0],
    venues[1].find((v) => v.partnerId === ch?.venue[1]) ?? venues[1][0],
  ];
  const feeOf = (v: SideVenue | undefined) => (mode === "real" && v ? v.feeRate : 0);
  const fees: [number, number] = [feeOf(picked[0]), feeOf(picked[1])];
  const fair = ladder ? fairFor(ladder, picked) : null;
  const plan: BidPlan | null =
    fair && picked[0] && picked[1]
      ? planBids(fair, marginPct / 100, [
          { capCents: picked[0].capCents, feeRate: fees[0] },
          { capCents: picked[1].capCents, feeRate: fees[1] },
        ])
      : null;
  const bids: [number, number] | null = plan ? [clampCents(ch?.bid[0] ?? plan.cents[0]), clampCents(ch?.bid[1] ?? plan.cents[1])] : null;
  const edited = ch != null && (ch.bid[0] != null || ch.bid[1] != null);
  const margin = bids ? pairMargin(bids, fees) : null;
  const contracts: [number, number] = ch ? (ch.perSide ? ch.contracts : [ch.contracts[0], ch.contracts[0]]) : [DEFAULT_CONTRACTS, DEFAULT_CONTRACTS];

  const legs = useMemo<QuoteLeg[]>(() => {
    if (!ladder || !bids || !ch) return [];
    const out: QuoteLeg[] = [];
    for (const col of [0, 1] as const) {
      const main = picked[col];
      if (!main) continue;
      const extra = venues[col].filter((v) => v.partnerId !== main.partnerId && v.partnerId in ch.extras[col]);
      const rows: { v: SideVenue; cents: number }[] = [
        { v: main, cents: bids[col] },
        ...extra.map((v) => ({ v, cents: clampCents(ch.extras[col][v.partnerId] ?? Math.min(bids[col], v.capCents)) })),
      ];
      const split = splitContracts(contracts[col], rows.length);
      rows.forEach((r, i) =>
        out.push({
          key: `${r.v.partnerId}|${col}`,
          col,
          sideLabel: ladder.columns[col].label,
          partnerId: r.v.partnerId,
          partnerName: r.v.partnerName,
          positionHash: ladder.columns[col].positionHash,
          liquidityHash: r.v.liquidityHash,
          cents: r.cents,
          contracts: split[i],
          feeRate: feeOf(r.v),
        }),
      );
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ladder, bids?.[0], bids?.[1], ch, picked[0], picked[1], contracts[0], contracts[1], venues, mode]);
  const placing = legs.filter((l) => l.contracts > 0);
  const existing = ownBook ? prefill?.existing : undefined;
  const result = useMemo(() => quoteResult(placing, existing), [placing, existing]);

  // ── placing ──
  const [cancelFirst, setCancelFirst] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orphan, setOrphan] = useState<PlacedLeg[] | null>(null);
  const restingIds = prefill?.restingIds ?? [];
  const cancels = requote && cancelFirst ? restingIds.length : 0;

  const realBlocked = wantsReal && !orders.canTradeReal;
  const canPlace = !blocked && !busy && orders.client === "ok" && !realBlocked && placing.length > 0 && !!ladder;

  // Real: the confirm freezes the legs it lists. If the market moved the computed bids by the time
  // Confirm is pressed, nothing is sent: the confirm shows the new bids and number, and asks again.
  const draft = placing.length > 0 ? draftKpi(result) : null;
  const [snap, setSnap] = useState<{ legs: QuoteLeg[]; kpi: number | null } | null>(null);
  const [movedNote, setMovedNote] = useState<string | null>(null);
  const openConfirm = () => {
    setSnap({ legs: placing, kpi: draft });
    setMovedNote(null);
    setConfirming(true);
  };
  const confirmPlace = () => {
    if (!snap) return;
    if (legsSig(snap.legs) !== legsSig(placing)) {
      setSnap({ legs: placing, kpi: draft });
      setMovedNote(
        `The market moved, so the bids were recomputed: ${placing.map((l) => `${l.sideLabel} ${fmtBid(l.cents, format)}`).join(" · ")}. If both fill: ${draft == null ? "—" : signedMoney(draft, ccy)}. Confirm again to place them.`,
      );
      return;
    }
    void place(snap.legs);
  };

  const place = async (toPlace: QuoteLeg[] = placing) => {
    if (!ladder || !market || !book || !canPlace || toPlace.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      if (cancels > 0) {
        const res = await orders.cancel(restingIds);
        if (res.errors?.length) throw new Error(`Couldn't cancel the resting orders first: ${res.errors.map((e) => e.error).join(" · ")}. Nothing new was placed.`);
      }
      const sent = buildQuoteLegs(toPlace, mode);
      // A new quote is filed under the Market making model (created here on the first quote, never
      // on page load). A re-quote joins its opportunity with that opportunity's own model, or none
      // when it has none (a quote from before models): any other model is a 400 model_mismatch.
      const modelId = requote ? (requote.modelId ?? null) : (await source.ensureModel()).modelId;
      const res = await orders.buy(sent, {
        ...(requote ? { opportunityId: requote.opportunityId } : {}),
        ...(modelId ? { modelId } : {}),
      });
      const placed = placedLegs(
        res,
        toPlace.map((l, i) => ({ ref: { positionHash: l.positionHash, partnerId: l.partnerId, partnerName: l.partnerName }, label: l.sideLabel, cents: l.cents, amount: sent[i].amount ?? 0 })),
      );
      const oppId = requote?.opportunityId ?? res.opportunity_id;
      if (!oppId) {
        // Nothing to track it under: show what happened here rather than losing it.
        setOrphan(placed);
        return;
      }
      if (requote) {
        source.addLegs(oppId, legRefsFrom(placed));
      } else {
        source.register({
          opportunityId: oppId,
          modelId: res.model_id ?? modelId ?? null,
          ccy,
          createdAt: Date.now(),
          contestId,
          game,
          marketKey: market.marketKey,
          bookKey: book.key,
          market: marketTitle(market, book),
          shape: market.shape,
          sides: ladder.columns.map((c) => ({ positionHash: c.positionHash, label: c.label, outcome: c.outcome })) as MmEntry["sides"],
          legs: legRefsFrom(placed),
        });
      }
      onPlaced(oppId, placed);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
      setConfirming(false);
      setSnap(null);
      setMovedNote(null);
    }
  };

  if (scope.loading) {
    return (
      <SoftCard className="flex flex-col gap-2 p-3">
        <Skeleton className="h-8 w-64 max-w-full" />
        <div className="grid gap-2 sm:grid-cols-2">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
      </SoftCard>
    );
  }

  const noVenue = ladder && (venues[0].length === 0 || venues[1].length === 0);
  const sideName = (i: 0 | 1) => ladder?.columns[i].label ?? "";

  return (
    <SoftCard className="flex w-full min-w-0 flex-col gap-3 p-3">
      {/* ── margin + contracts ── */}
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className={`flex flex-wrap items-center gap-1.5 ${coachRing(coach, "margin")}`} role="group" aria-label="Margin after fees">
            <span className="mr-0.5 text-[12px] text-dim">Margin</span>
            {MARGIN_PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={marginPct === p && customText === ""}
                onClick={() => {
                  setCustomText("");
                  setMargin(p);
                }}
                className={`nums min-h-11 rounded-full border px-3 text-[13px] font-medium transition-colors duration-150 motion-reduce:transition-none lg:min-h-8 ${
                  marginPct === p && customText === "" ? "border-amber/60 bg-amber/15 text-amber" : "border-border-2 bg-panel-2 text-dim hover:bg-raised hover:text-ink"
                }`}
              >
                {p}%
              </button>
            ))}
            <label className={`flex min-h-11 items-center rounded-full border px-2.5 lg:min-h-8 ${customText !== "" ? "border-amber/60 bg-amber/10" : "border-border-2 bg-panel-2"}`}>
              <span className="sr-only">Custom margin, percent</span>
              <input
                inputMode="decimal"
                value={customText}
                placeholder="Custom"
                onChange={(e) => {
                  setCustomText(e.target.value);
                  const n = Number(e.target.value.replace(/[%\s]/g, ""));
                  if (e.target.value.trim() !== "" && Number.isFinite(n) && n > 0 && n <= MAX_MARGIN_PCT) setMargin(n);
                }}
                className="nums w-14 bg-transparent text-right text-[13px] text-ink outline-none placeholder:text-faint"
              />
              <span className="text-[12px] text-dim">%</span>
            </label>
          </div>
          <div className={`flex items-center gap-2 ${coachRing(coach, "contracts")}`}>
            <span className="text-[12px] text-dim">Contracts {ch?.perSide ? "per side" : "a side"}</span>
            {ch && !ch.perSide ? (
              <ContractsInput value={ch.contracts[0]} onChange={(n) => patch((c) => ({ ...c, contracts: [n, n] }))} label="Contracts on each side" />
            ) : (
              <span className="text-[11px] text-faint">set under Options</span>
            )}
          </div>
        </div>
        {margin != null && (edited || Math.abs(margin * 100 - marginPct) >= 0.05) && (
          <span className={`nums text-[12px] ${margin <= 0 ? "text-down" : "text-dim"}`} aria-live="polite">
            {edited ? "Your bids give" : "Bids give"} <span className="font-semibold text-ink">{pctText(margin)}</span>
            {edited && (
              <button type="button" onClick={() => setMargin(marginPct)} className="ml-2 text-[11px] text-faint underline-offset-2 hover:text-ink hover:underline">
                reset to {marginPct}%
              </button>
            )}
          </span>
        )}
        <CoachTip coach={coach} step="margin">
          The <b>margin</b> is what you keep from every $1 a pair pays out if both bids fill, after the venues' fees. 2% means you pay 98¢ for a
          pair that always pays $1. greenbook works out the two bids from it.
        </CoachTip>
        <CoachTip coach={coach} step="contracts">
          <b>Contracts</b> are how many you bid for on each side. Every contract pays $1 if its side wins, so {contracts[0]} a side at a {marginPct}%
          margin earns about {money((contracts[0] * marginPct) / 100, ccy)} if both fill.
        </CoachTip>
      </div>

      {/* ── the two computed bids ── */}
      {ladder && !noVenue && bids && ch ? (
        <div className="flex flex-col gap-2">
          {([0, 1] as const).map((col) => (
            <SideCard
              key={col}
              label={sideName(col)}
              outcome={ladder.columns[col].outcome}
              venues={venues[col]}
              picked={picked[col]!}
              bid={bids[col]}
              clamp={ch.bid[col] == null ? (plan?.clamped[col] ?? null) : null}
              feeRate={fees[col]}
              contracts={contracts[col]}
              onBid={(c) => patch((x) => ({ ...x, bid: (col === 0 ? [c, x.bid[1]] : [x.bid[0], c]) as [number | null, number | null] }))}
              onVenue={(id) =>
                patch((x) => {
                  const extras = [{ ...x.extras[0] }, { ...x.extras[1] }] as DeskChoice["extras"];
                  delete extras[col][id];
                  return { ...x, venue: (col === 0 ? [id, x.venue[1]] : [x.venue[0], id]) as [string | null, string | null], bid: (col === 0 ? [null, x.bid[1]] : [x.bid[0], null]) as [number | null, number | null], extras };
                })
              }
            />
          ))}
        </div>
      ) : ladder ? (
        <div className="rounded-md border border-border bg-bg p-3 text-[13px] text-dim">
          {mode === "real"
            ? `None of your connected venues takes a limit on ${venues[0].length === 0 ? sideName(0) || "one side" : sideName(1) || "one side"} here. Switch to Paper, pick another market above, or connect a venue on OpenMarkets.`
            : "No venue quotes both sides of this market right now. Pick another market above."}
        </div>
      ) : null}

      {plan?.short && !edited && (
        <p className="text-[12px] text-down">
          {marginPct}% isn't possible here: bids can't go under {fmtBid(2, "cents")}, so the most this market allows is {pctText(plan.margin)}.
        </p>
      )}
      {plan?.over && !edited && <p className="text-[12px] text-faint">Held 1¢ under market → more than {marginPct}%</p>}

      {/* ── the big amber Draft number ── */}
      <div className={coachRing(coach, "result")}>
        <DraftKpi result={result} value={draft} margin={margin} mode={mode} labels={[sideName(0), sideName(1)]} ccy={ccy} existing={existing} />
        <CoachTip coach={coach} step="result">
          The big amber number is a <b>Draft</b>: what you make <b>if both bids fill</b>. Once placed it turns blue (<b>Working</b>). If only one
          side fills, the card shows <b>Get out now</b>: what you'd lock in by buying the other side straight away.
        </CoachTip>
      </div>

      {/* ── Place ── */}
      <div className={`flex flex-col gap-2 ${coachRing(coach, "place")}`}>
        {requote && restingIds.length > 0 && (
          <label className="flex min-h-11 items-center gap-2 text-[13px] text-ink lg:min-h-8">
            <input type="checkbox" checked={cancelFirst} onChange={(e) => setCancelFirst(e.target.checked)} className="h-4 w-4 accent-amber" />
            Cancel its {restingIds.length} resting order{restingIds.length === 1 ? "" : "s"} first
          </label>
        )}

        {orders.client !== "ok" ? (
          <p className="text-[12px] text-amber">
            {orders.client === "signed_out" ? "Sign in with OpenMarkets to place orders." : "Placing orders needs a Sign in with OpenMarkets session (a pasted API key can't place orders)."}
          </p>
        ) : realBlocked ? (
          <div className="flex flex-wrap items-center gap-2 text-[12px] text-amber">
            <span className="min-w-0 flex-1">Real orders need order placement access, which this session wasn't granted.</span>
            <button type="button" onClick={orders.unlockReal} className={`${btnBase} ${btnPrimary}`}>
              Unlock Real
            </button>
          </div>
        ) : null}

        {error && <p className="text-[12px] text-down">{error}</p>}

        {confirming && snap ? (
          <>
            {movedNote && <p className="text-[12px] font-medium text-amber">{movedNote}</p>}
            <ConfirmOrders
              lines={snap.legs.map((l) => ({ key: l.key, what: l.sideLabel, venue: l.partnerName, cents: l.cents, amount: buildQuoteLegs([l], mode)[0]?.amount ?? 0, type: "limit", contracts: l.contracts }))}
              cancels={cancels}
              ccy={ccy}
              busy={busy}
              onBack={() => {
                setConfirming(false);
                setSnap(null);
                setMovedNote(null);
              }}
              onConfirm={confirmPlace}
            />
          </>
        ) : (
          <button
            type="button"
            disabled={!canPlace}
            onClick={() => (mode === "real" ? openConfirm() : void place())}
            title={
              mode === "real"
                ? "Limit orders at your connected venues; each rests until it fills or you cancel it"
                : "Paper limit orders rest on the practice book (OpenMarkets), not at the venue you pick"
            }
            className={`${btnBase} ${btnPrimary} w-full !text-[14px] font-semibold lg:!min-h-10`}
          >
            {busy ? "Placing…" : `${mode === "real" ? "Review" : "Place"} ${placing.length} ${mode === "real" ? "real " : ""}bid${placing.length === 1 ? "" : "s"}`}
          </button>
        )}
        <CoachTip coach={coach} step="place">
          <b>Place</b> sends both bids as limit orders. They wait until someone sells to you, so nothing fills straight away. Then you watch them
          on the Market making page.
        </CoachTip>
        {orphan && <PlacedResult legs={orphan} ccy={ccy} note="The API returned no opportunity id, so greenbook can't track these here. They're in Portfolio." />}
      </div>

      <Options
        ladder={ladder}
        choice={ch}
        venues={venues}
        picked={picked}
        bids={bids}
        patch={patch}
      />
    </SoftCard>
  );
}

/** Legs as a comparable string (venue, side, price, contracts). */
const legsSig = (ls: QuoteLeg[]): string => ls.map((l) => `${l.partnerId}:${l.col}:${l.cents}:${l.contracts}`).join(",");

const nullBids = (r: Record<string, number | null>): Record<string, number | null> => Object.fromEntries(Object.keys(r).map((k) => [k, null]));

/** Defaults (auto venues, computed bids, 10 a side), or a re-quote's resting bids when they land here. */
function initialChoice(id: string, venues: [SideVenue[], SideVenue[]], prefill?: QuotePrefill): DeskChoice {
  const c: DeskChoice = { id, venue: [null, null], bid: [null, null], extras: [{}, {}], contracts: [DEFAULT_CONTRACTS, DEFAULT_CONTRACTS], perSide: false };
  if (!prefill || prefill.bids.length === 0) return c;
  const total: [number, number] = [0, 0];
  for (const col of [0, 1] as const) {
    const bids = prefill.bids.filter((b) => b.col === col && venues[col].some((v) => v.partnerId === b.partnerId));
    if (bids.length === 0) continue;
    c.venue[col] = bids[0].partnerId;
    c.bid[col] = bids[0].cents;
    for (const b of bids.slice(1)) if (b.partnerId !== bids[0].partnerId) c.extras[col][b.partnerId] = b.cents;
    total[col] = bids.reduce((n, b) => n + b.contracts, 0);
  }
  if (total[0] > 0 || total[1] > 0) {
    c.contracts = [total[0] || DEFAULT_CONTRACTS, total[1] || DEFAULT_CONTRACTS];
    c.perSide = c.contracts[0] !== c.contracts[1];
  }
  return c;
}

/* ================================================================= side card */

/** One side, compact: name + market now, then the computed bid ‹ › and its venue. */
function SideCard({
  label,
  outcome,
  venues,
  picked,
  bid,
  clamp,
  feeRate,
  contracts,
  onBid,
  onVenue,
}: {
  label: string;
  outcome: string;
  venues: SideVenue[];
  picked: SideVenue;
  bid: number;
  clamp: BidPlan["clamped"][number];
  feeRate: number;
  contracts: number;
  onBid: (c: number) => void;
  onVenue: (partnerId: string) => void;
}) {
  const { format } = useOddsFormat();
  const crosses = bid > picked.capCents;
  // One short tag instead of a sentence; the full reason is the tooltip.
  const fee = feeRate > 0 ? `fee ≈${Math.round(feeRate * 1000) / 10}%` : null;
  const note = crosses ? "fills now" : clamp === "ask" ? "1¢ under mkt" : clamp === "floor" ? "min 2¢" : clamp === "ceiling" ? "max 98¢" : fee;
  const noteTitle = crosses
    ? "At or above the market: this would fill now as a taker instead of resting"
    : clamp === "ask"
      ? "Held 1¢ under the market so it rests; the rest of the margin moved to the other side"
      : fee
        ? "Maker fee, ≈ of the stake"
        : undefined;
  return (
    <div className="flex min-w-0 flex-col gap-1.5 rounded-md border border-border/70 bg-panel px-2.5 py-2">
      <div className="flex min-w-0 items-baseline gap-2">
        <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-ink" title={outcome}>
          {label}
        </span>
        {note && (
          <span className={`shrink-0 text-[11px] ${crosses ? "text-amber" : "text-faint"}`} title={noteTitle}>
            {note}
          </span>
        )}
        <span className="nums shrink-0 text-[12px] text-dim" title={`Market now on ${picked.partnerName} · ${fmtContracts(contracts)} contracts`}>
          mkt {fmtPrice(picked.ask, picked.american, format)}
        </span>
      </div>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <CentsStepper cents={bid} onChange={(c) => onBid(clampCents(c))} label={`${label} bid`} />
        <label className="flex min-w-0 flex-1 basis-32 items-center gap-1.5 text-[12px] text-dim">
          <span className="shrink-0">on</span>
          <VenueLogo name={picked.partnerName} size={16} />
          <select
            value={picked.partnerId}
            onChange={(e) => onVenue(e.target.value)}
            aria-label={`${label}: venue`}
            className="min-h-11 min-w-0 flex-1 truncate rounded-md border border-border-2 bg-bg px-2 text-[13px] text-ink lg:min-h-8"
          >
            {venues.map((v) => (
              <option key={v.partnerId} value={v.partnerId}>
                {v.partnerName} · {fmtPrice(v.ask, v.american, format)}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}

/* ================================================================ draft KPI */

/**
 * The builder's big amber Draft number (MmKpi): what you make if both sides fill, with the
 * margin it is, then the risk if only one side fills and the total cost (small).
 */
function DraftKpi({
  result,
  value,
  margin,
  mode,
  labels,
  ccy,
  existing,
}: {
  result: QuoteResult;
  value: number | null;
  margin: number | null;
  mode: MoneyMode;
  labels: [string, string];
  ccy: Ccy;
  existing: Existing | undefined;
}) {
  if (value == null) return <MmKpi state="draft" value={null} ccy={ccy} caption="Pick a market with prices on both sides to see what you'd make." />;
  const even = Math.abs(result.bothFill[0] - result.bothFill[1]) < 0.005;
  const held = existing && (existing.filled[0] > 0 || existing.filled[1] > 0);
  const s$ = (n: number) => <span className={`nums font-medium ${toneOf(n)}`}>{signedMoney(n, ccy)}</span>;
  // The risk in one number: the worst outcome if only one side's bids fill (full detail on hover).
  const worst = Math.min(result.onlyFill[0][0], result.onlyFill[0][1], result.onlyFill[1][0], result.onlyFill[1][1]);
  const onlyTitle = ([0, 1] as const)
    .map((s) => {
      const w = s === 0 ? 1 : 0;
      return `Only ${labels[s]} fills: ${signedMoney(result.onlyFill[s][s], ccy)} if ${labels[s]} wins, ${signedMoney(result.onlyFill[s][w], ccy)} if ${labels[w]} wins`;
    })
    .join("\n");
  return (
    <MmKpi
      state="draft"
      value={value}
      ccy={ccy}
      caption={
        <>
          if both fill{even ? "" : ", at least"}
          {margin != null && <span className="nums"> · {pctText(margin)}{mode === "real" ? " after fees" : ""}</span>}
        </>
      }
    >
      <span className="nums" title={onlyTitle}>
        if one side fills {s$(worst)} worst · cost {money(result.cost, ccy)}
        {result.fees > 0 && <span className="text-faint"> · fees ≈{money(result.fees, ccy)}</span>}
      </span>
      {held && (
        <span className="nums text-faint">
          incl.{" "}
          {([0, 1] as const)
            .filter((i) => existing!.filled[i] > 0)
            .map((i) => `${fmtContracts(existing!.filled[i])} ${labels[i]}`)
            .join(" + ")}{" "}
          filled
        </span>
      )}
    </MmKpi>
  );
}

/* =================================================================== options */

function Options({
  ladder,
  choice,
  venues,
  picked,
  bids,
  patch,
}: {
  ladder: Ladder | null;
  choice: DeskChoice | null;
  venues: [SideVenue[], SideVenue[]];
  picked: [SideVenue | undefined, SideVenue | undefined];
  bids: [number, number] | null;
  patch: (fn: (c: DeskChoice) => DeskChoice) => void;
}) {
  const [open, setOpen] = useState(false);
  const { format } = useOddsFormat();
  const extrasOn = choice ? Object.keys(choice.extras[0]).length + Object.keys(choice.extras[1]).length : 0;
  const summary = [
    choice?.perSide ? "contracts per side" : null,
    extrasOn > 0 ? `+${extrasOn} venue${extrasOn === 1 ? "" : "s"}` : null,
  ].filter(Boolean);

  return (
    <div className="border-t border-border/60 pt-2">
      <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="flex min-h-11 w-full items-center gap-2 text-left text-[13px] text-dim hover:text-ink lg:min-h-8">
        <span className={`text-[10px] transition-transform duration-150 motion-reduce:transition-none ${open ? "rotate-90" : ""}`}>▸</span>
        Options
        {summary.length > 0 && <span className="truncate text-[11px] text-faint">· {summary.join(" · ")}</span>}
      </button>
      {open && (
        <div className="gb-fade mt-1 flex flex-col gap-3">
          {choice && ladder && (
            <Row label="Contracts">
              <label className="flex min-h-11 items-center gap-2 text-[13px] text-ink lg:min-h-8">
                <input
                  type="checkbox"
                  checked={choice.perSide}
                  onChange={(e) => patch((c) => ({ ...c, perSide: e.target.checked, contracts: e.target.checked ? c.contracts : [c.contracts[0], c.contracts[0]] }))}
                  className="h-4 w-4 accent-amber"
                />
                Different on each side
              </label>
              {choice.perSide &&
                ([0, 1] as const).map((col) => (
                  <span key={col} className="flex items-center gap-1.5 text-[12px] text-dim">
                    <span className="max-w-[7rem] truncate">{ladder.columns[col].label}</span>
                    <ContractsInput
                      compact
                      value={choice.contracts[col]}
                      onChange={(n) => patch((c) => ({ ...c, contracts: (col === 0 ? [n, c.contracts[1]] : [c.contracts[0], n]) as [number, number] }))}
                      label={`${ladder.columns[col].label} contracts`}
                    />
                  </span>
                ))}
            </Row>
          )}

          {choice && ladder && bids && (
            <div className="flex flex-col gap-1.5">
              <div className="text-[12px] text-dim" title="A side's contracts split across every venue switched on">
                More venues
              </div>
              <div className="flex flex-col gap-2">
                {([0, 1] as const).map((col) => (
                  <ul key={col} className="flex min-w-0 flex-col divide-y divide-border/50 rounded-md border border-border/70 bg-bg" aria-label={`${ladder.columns[col].label} venues`}>
                    <li className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-faint">{ladder.columns[col].label}</li>
                    {venues[col].map((v) => {
                      const main = v.partnerId === picked[col]?.partnerId;
                      const on = main || v.partnerId in choice.extras[col];
                      const cents = main ? bids[col] : clampCents(choice.extras[col][v.partnerId] ?? Math.min(bids[col], v.capCents));
                      return (
                        <li key={v.partnerId} className="flex min-w-0 items-center gap-2 px-2 py-1">
                          <input
                            type="checkbox"
                            checked={on}
                            disabled={main}
                            onChange={(e) =>
                              patch((c) => {
                                const extras = [{ ...c.extras[0] }, { ...c.extras[1] }] as DeskChoice["extras"];
                                if (e.target.checked) extras[col][v.partnerId] = null;
                                else delete extras[col][v.partnerId];
                                return { ...c, extras };
                              })
                            }
                            aria-label={`Bid on ${v.partnerName}`}
                            className="h-4 w-4 shrink-0 accent-amber disabled:opacity-60"
                          />
                          <span className="min-w-0 flex-1 truncate text-[12px] text-ink" title={main ? "The side card's venue" : undefined}>
                            {v.partnerName}
                            {main && <span className="text-faint"> · main</span>}
                          </span>
                          <span className="nums shrink-0 text-[11px] text-faint" title="Market now">
                            {fmtPrice(v.ask, v.american, format)}
                          </span>
                          {on && !main ? (
                            <CentsStepper
                              compact
                              cents={cents}
                              onChange={(n) =>
                                patch((c) => {
                                  const extras = [{ ...c.extras[0] }, { ...c.extras[1] }] as DeskChoice["extras"];
                                  extras[col][v.partnerId] = clampCents(n);
                                  return { ...c, extras };
                                })
                              }
                              label={`${v.partnerName} ${ladder.columns[col].label} bid`}
                            />
                          ) : (
                            <span className={`nums w-[5.5rem] shrink-0 text-right text-[12px] ${on ? "text-ink" : "text-faint"}`}>{on ? fmtBid(cents, format) : "off"}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-20 shrink-0 text-[12px] text-dim">{label}</span>
      {children}
    </div>
  );
}
